import styles from './GameReport.module.css'
import classNames from 'classnames'
import { formatTime } from '../../../../util/number'
import { t } from 'i18next'
import { GameReportSummaryData } from '../../../../contexts/ReportsContextHelper'
import { Game } from '../../../../types/commonTypes'
import { BadgeImage } from '../Badges/BadgeView/BadgeImage'
import { getIcon, IconName } from '../../../../common/components/icons/utils'
import { Tooltip } from '../../../../common/components/tooltip/Tooltip'
import Chart from 'react-google-charts'
import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { ReportSortDir } from '../../../../contexts/ReportsContextProvider'
import { noop } from '../../../../util/functional'

type SummaryTabProps = {
  summaryData?: GameReportSummaryData
  onBadgeClick: (badgeId: number) => void
  gameData?: Game
  onViewPlayers: () => void
  onJumpToTasks: (sortDir: ReportSortDir) => void
}

export const SummaryTab: React.FC<SummaryTabProps> = ({
  summaryData,
  gameData,
  onBadgeClick,
  onViewPlayers,
  onJumpToTasks,
}) => {
  const handleBadgeClickInternal = useCallback(
    (badgeId: number) => () => {
      onBadgeClick(badgeId)
    },
    [onBadgeClick],
  )

  const badgesGridRef = useRef<HTMLDivElement>(null)
  const [useTwoColumns, setUseTwoColumns] = useState(false)

  useLayoutEffect(() => {
    const grid = badgesGridRef.current
    if (!grid) return
    const titles = grid.querySelectorAll('[data-badge-title]')
    let overflow = false
    titles.forEach((el) => {
      const htmlEl = el as HTMLElement
      if (htmlEl.scrollWidth > htmlEl.clientWidth) {
        overflow = true
      }
    })
    setUseTwoColumns(overflow)
  }, [summaryData?.awardedBadges])

  const handleJumpToDifficultFirst = useCallback(() => {
    onJumpToTasks(ReportSortDir.ASC)
  }, [onJumpToTasks])

  const handleJumpToEasyFirst = useCallback(() => {
    onJumpToTasks(ReportSortDir.DESC)
  }, [onJumpToTasks])

  return (
    <>
      {gameData?.advancedSettings.noPointsGame ? (
        <div className={styles.summaryDataBoxesRow}>
          <SummaryDataBox
            pieChartPercentage={summaryData?.completionRate}
            isDonut
            value={summaryData?.completionRate.toFixed() + '%'}
            valueInfo={t('game_summary.completion_rate_value_info', 'finished the game')}
            valueInfoColor='green'
            mainLabel={t('game_summary.completion_rate', 'Completion rate')}
            subLabel={t('game_summary.completion_rate_sub_text', 'of all players')}
            toolTipText={t('game_summary.completion_rate_tooltip', 'Percentage of all players to complete the game')}
          />
          <SummaryDataBox
            iconName='multipleUsers'
            value={summaryData?.playerCount}
            valueInfo={t('game_summary.player_count_value_info', 'players or teams')}
            valueInfoColor='pink'
            mainLabel={t('game_summary.player_count', 'Total players')}
            toolTipText={t('game_summary.player_count_tooltip', 'Total players in the game')}
          />
          <SummaryDataBox
            iconName='timerGradient'
            greyIcon
            value={summaryData?.playersNotFinished.slice(0, 4)}
            valueInfo={t('game_summary.view_all', 'View all')}
            valueInfoColor='pink'
            mainLabel={t('game_summary.players_not_finished', 'Players not finished (%{amount})', {
              amount: summaryData?.playersNotFinished.length,
            })}
            toolTipText={t('game_summary.not_completed_tooltip', 'Players who did not complete the game')}
            onClick={onViewPlayers}
          />
        </div>
      ) : (
        <>
          <div className={styles.summaryDataBoxesRow}>
            <SummaryDataBox
              pieChartPercentage={summaryData?.completionRate}
              isDonut
              value={summaryData?.completionRate.toFixed() + '%'}
              valueInfo={t('game_summary.completion_rate_value_info', 'finished the game')}
              valueInfoColor='green'
              mainLabel={t('game_summary.completion_rate', 'Completion rate')}
              subLabel={t('game_summary.completion_rate_sub_text', 'of all players')}
              toolTipText={t('game_summary.completion_rate_tooltip', 'Percentage of all players to complete the game')}
            />
            <SummaryDataBox
              iconName='chartBarIcon'
              biggerIcon
              value={summaryData?.averageScore.toFixed() + ' / ' + gameData?.maxPoints}
              valueInfo={t('game_summary.score_value_infotext', 'gained points vs maximum')}
              mainLabel={t('game_summary.average_score', 'Average score')}
              toolTipText={t('game_summary.average_score_tooltip', 'Average score vs game max score')}
            />
            <SummaryDataBox
              iconName='multipleUsers'
              value={summaryData?.playerCount}
              valueInfo={t('game_summary.player_count_value_info', 'players or teams')}
              valueInfoColor='pink'
              mainLabel={t('game_summary.player_count', 'Total players')}
              toolTipText={t('game_summary.player_count_tooltip', 'Total players in the game')}
            />
          </div>
          <div className={styles.summaryDataBoxesRow}>
            <SummaryDataBox
              pieChartPercentage={summaryData?.mostSuccesfulTask.correctPercentage}
              iconName={summaryData?.mostSuccesfulTask.correctPercentage === 100 ? 'averageFullIcon' : undefined}
              biggerIcon
              averageTime={summaryData?.mostSuccesfulTask.averageTime}
              mainLabel={t('game_summary.succesful_task', 'Most succesful task')}
              subLabelColor='green'
              subLabel={summaryData?.mostSuccesfulTask.name}
              toolTipText={t('game_summary.succesul_task_tooltip', 'Task there the teams got highest average score')}
              onJumpToTasks={handleJumpToEasyFirst}
            />
            <SummaryDataBox
              pieChartPercentage={summaryData?.mostDifficultTask.correctPercentage}
              iconName={(summaryData?.mostDifficultTask.correctPercentage ?? 0) < 1 ? 'averageZeroIcon' : undefined}
              biggerIcon
              averageTime={summaryData?.mostDifficultTask.averageTime}
              mainLabel={t('game_summary.difficult_task', 'Most difficult task')}
              subLabelColor='pink'
              subLabel={summaryData?.mostDifficultTask.name}
              toolTipText={t('game_summary.difficult_task_tooltip', 'Task there the teams got lowest average score')}
              onJumpToTasks={handleJumpToDifficultFirst}
            />
            <SummaryDataBox
              iconName='timerGradient'
              greyIcon
              value={summaryData?.playersNotFinished.slice(0, 4)}
              valueInfo={t('game_summary.view_all', 'View all')}
              valueInfoColor='pink'
              mainLabel={t('game_summary.players_not_finished', 'Players not finished (%{amount})', {
                amount: summaryData?.playersNotFinished.length,
              })}
              toolTipText={t('game_summary.not_completed_tooltip', 'Players who did not complete the game')}
              onClick={onViewPlayers}
            />
          </div>
        </>
      )}
      {(summaryData?.awardedBadges ?? []).length > 0 && (
        <div className={styles.badgesArea}>
          <div className={styles.gameTitle}>{t('game_summary.badges_title', 'Badges awarded')}</div>
          <div className={styles.gameBasics}>
            {t('game_summary.badges_info_row', 'Click badge to see list of players it was awarded to')}
          </div>
          <div ref={badgesGridRef} className={classNames(styles.badgesGrid, useTwoColumns && styles.badgesGridTwoColumns)}>
            {summaryData?.awardedBadges.map((badge) => {
              const awardedPercentage = ((100 * badge.count) / summaryData.playerCount).toFixed()
              return (
                <div className={styles.badgeSlot} key={badge.id}>
                  <div className={styles.clickableBadge} onClick={handleBadgeClickInternal(badge.id)}>
                    <BadgeImage url={badge.imageUrl} />
                  </div>
                  <div className={styles.badgeRightSide}>
                    <div data-badge-title className={styles.badgeTitle}>{badge.name}</div>
                    <div className={styles.gameBasics}>
                      {awardedPercentage + '% ' + t('game_summary.awarded', 'awarded')}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}

interface SummaryDataBoxProps {
  iconName?: IconName
  isDonut?: boolean
  pieChartPercentage?: number
  averageTime?: number
  greyIcon?: boolean
  biggerIcon?: boolean
  value?: number | string | string[]
  valueInfo?: string
  valueInfoColor?: 'neutral' | 'pink' | 'green'
  mainLabel: string
  subLabel?: string
  subLabelColor?: 'neutral' | 'pink' | 'green'
  toolTipText?: string
  onClick?: () => void
  onJumpToTasks?: () => void
}

export const SummaryDataBox: React.FC<SummaryDataBoxProps> = ({
  iconName,
  pieChartPercentage,
  averageTime,
  isDonut,
  greyIcon,
  biggerIcon,
  value,
  valueInfo,
  valueInfoColor,
  mainLabel,
  subLabel,
  subLabelColor,
  toolTipText,
  onClick,
  onJumpToTasks,
}) => {
  const pieOptions = {
    legend: 'none',
    pieHole: isDonut ? 0.75 : 0,
    pieSliceText: 'none',
    tooltip: { trigger: 'none' },
    chartArea: { width: '100%', height: '100%' },
    slices: {
      0: { color: (pieChartPercentage ?? 0) < 50 ? '#F5C172' : '#69AE97' }, //Yellow-100 and green-100
      1: { color: '#EBEBEB' }, //grey-200
    },
  }
  return (
    <Tooltip key={iconName} tooltipContent={toolTipText + ''}>
      {(tooltipProps) => (
        <div
          onClick={onJumpToTasks ?? noop}
          className={classNames(styles.summaryDataBox, onJumpToTasks && styles.clickable)}
          {...tooltipProps}
        >
          <div>
            <div className={styles.summaryBoxMainLabel}>{mainLabel}</div>
            <div
              className={classNames(
                styles.summaryBoxSubLabel,
                subLabelColor === 'pink' && styles.summaryBoxSubLabelPink,
                subLabelColor === 'green' && styles.summaryBoxSubLabelGreen,
              )}
            >
              {subLabel}
            </div>
          </div>
          <div>
            {['number', 'string'].includes(typeof value) && (
              <div
                className={classNames(
                  styles.summaryBoxLargeText,
                  (value + '').length > 12 && styles.summaryBoxSmallerText,
                  (value + '').length > 15 && styles.summaryBoxSmallestText,
                )}
              >
                {value}
              </div>
            )}
            {typeof value == 'object' && (
              <div className={styles.notFinishedPlayers}>
                {value.map((item) => {
                  return <div key={item}>{item}</div>
                })}
              </div>
            )}
            {valueInfo && (
              <div
                onClick={onClick}
                className={classNames(
                  styles.summaryBoxSubLabel,
                  onClick && styles.clickableBadge,
                  valueInfoColor === 'pink' && styles.summaryBoxSubLabelPink,
                  valueInfoColor === 'green' && styles.summaryBoxSubLabelGreen,
                )}
              >
                {valueInfo}
              </div>
            )}
            {averageTime !== undefined && (
              <div>
                <div className={styles.chartInfoRow}>
                  <span>{pieChartPercentage?.toFixed() + '%'}</span>
                  {t('game_summary.average_of_max', 'average points of max')}
                </div>
                <div className={styles.chartInfoRow}>
                  <span>{formatTime(averageTime)}</span>
                  {t('game_summary.average_time', 'average answer time')}
                </div>
              </div>
            )}
          </div>
          {iconName && (
            <div
              className={classNames(
                styles.summaryDataIconContainer,
                greyIcon && styles.greyIcon,
                biggerIcon && styles.biggerIcon,
              )}
            >
              {getIcon(iconName)}
            </div>
          )}
          {pieChartPercentage !== undefined && !iconName && (
            <div className={styles.summaryDataGraphContainer}>
              <Chart
                chartType='PieChart'
                width='100%'
                height='100%'
                options={pieOptions}
                data={[
                  ['isCorrect', 'value'],
                  ['Correct', Math.min(100, pieChartPercentage)],
                  ['Not correct', 100 - Math.min(100, pieChartPercentage)],
                ]}
              />
            </div>
          )}
        </div>
      )}
    </Tooltip>
  )
}
